import { FC, useState, useMemo } from "react";
import { DropDownDeadlineSelect, DropDownMultiSelect } from "@ui/drop-down";
import { SELECTS_ID } from "@store/company/procedure-instrunctions-store";
import styles from "./medical-exam-controls-filters.module.scss";
import { DatePeriod } from "@ui/date-period";
import { useDisclosure } from "@mantine/hooks";
import CalendarMonth from "@mui/icons-material/CalendarMonth";

enum FilterType {
  Companies = "companies",
  Departments = "departments",
  Positions = "positions",
  Factors = "factors",
  Employees = "employees",
  Categories = "categories",
}

export type SelectList = {
  id: number;
  title: string;
};

export type MedicalExamControlsFiltersPropsT = {
  departments: SelectList[];
  positions: SelectList[];
  employees: SelectList[];
  factors: SelectList[];
  companies?: SelectList[];
  categories?: SelectList[];
  selectedDepartments: SelectList[];
  selectedPositions: SelectList[];
  selectedEmployees: SelectList[];
  selectedFactors: SelectList[];
  selectedCompanies?: SelectList[];
  selectedCategory?: SelectList[];
  onDepartmentsChange: (values: SelectList[]) => void;
  onPositionChange: (values: SelectList[]) => void;
  onEmployeeChange: (values: SelectList[]) => void;
  onFactorChange: (values: SelectList[]) => void;
  onStartDateChange: (value: number[] | undefined) => void;
  onDeadlineChange: (values: number[]) => void;
  onCompaniesChange?: (values: SelectList[]) => void;
  onCategoriesChange?: (values: SelectList[]) => void;
};

export const MedicalExamControlsFilters: FC<
  MedicalExamControlsFiltersPropsT
> = ({
  departments,
  positions,
  employees,
  factors,
  companies,
  categories,
  selectedDepartments,
  selectedPositions,
  selectedEmployees,
  selectedFactors,
  selectedCompanies,
  selectedCategory,
  onDepartmentsChange,
  onPositionChange,
  onEmployeeChange,
  onFactorChange,
  onStartDateChange,
  onDeadlineChange,
  onCompaniesChange,
  onCategoriesChange,
}) => {
  const handleChangeFilter = (key: string, values: SelectList[]) => {
    switch (key) {
      case FilterType.Departments:
        onDepartmentsChange(values);
        break;
      case FilterType.Positions:
        onPositionChange(values);
        break;
      case FilterType.Employees:
        onEmployeeChange(values);
        break;
      case FilterType.Factors:
        onFactorChange(values);
        break;
      case FilterType.Companies:
        onCompaniesChange?.(values);
        break;
      case FilterType.Categories:
        onCategoriesChange?.(values);
        break;
    }
  };

  const handleChangeStartDate = (value: number[] | undefined) => {
    if (value && value.length < 2) return;
    onStartDateChange(value);
  };

  const handleChangeDeadline = (value: number[]) => onDeadlineChange(value);

  const departmentsList = useMemo(() => {
    return [
      {
        title: "Все подразделения",
        id: SELECTS_ID,
      },
      ...(departments?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departments]);
  const positionsList = useMemo(() => {
    return [
      {
        title: "Все должности",
        id: SELECTS_ID,
      },
      ...(positions?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions]);
  const employeesList = useMemo(() => {
    return [
      {
        title: "Все сотрудники",
        id: SELECTS_ID,
      },
      ...(employees?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);
  const categoriesList = useMemo(() => {
    return [
      {
        title: "Все категории",
        id: SELECTS_ID,
      },
      ...(categories?.map((item) => ({
        title: item.title,
        id: item.id,
      })) ?? []),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  return (
    <div className={styles.filters}>
      {companies && onCompaniesChange && selectedCompanies && (
        <DropDownMultiSelect
          size={"medium"}
          typeStyle={"input"}
          defaultTitle={"Все компании"}
          direction={companies}
          // defaultValue={selectedCompanies.map(e => e.id)}
          allId={SELECTS_ID}
          handlerList={(values) => handleChangeFilter("companies", values)}
        />
      )}
      <DropDownMultiSelect
        size={"medium"}
        typeStyle={"input"}
        defaultTitle={"Все подразделения"}
        direction={departmentsList}
        // defaultValue={selectedDepartments.map(e => e.id)}
        allId={SELECTS_ID}
        handlerList={(values) => handleChangeFilter("departments", values)}
      />
      <DropDownMultiSelect
        size={"medium"}
        typeStyle={"input"}
        defaultTitle={"Все должности"}
        direction={positionsList}
        // defaultValue={selectedPositions.map(e => e.id)}
        allId={SELECTS_ID}
        handlerList={(values) => handleChangeFilter("positions", values)}
      />
      <DropDownMultiSelect
        size={"medium"}
        typeStyle={"input"}
        defaultTitle={"Все сотрудники"}
        direction={employeesList}
        // defaultValue={selectedEmployees.map(e => e.id)}
        allId={SELECTS_ID}
        handlerList={(values) => handleChangeFilter("employees", values)}
      />
      <DropDownMultiSelect
        size={"medium"}
        typeStyle={"input"}
        defaultTitle={"Все факторы"}
        direction={factors}
        // defaultValue={selectedFactors.map(e => e.id)}
        allId={SELECTS_ID}
        handlerList={(values) => handleChangeFilter("factors", values)}
      />
      <DropDownMultiSelect
        size={"medium"}
        typeStyle={"input"}
        defaultTitle={"Все категории"}
        direction={categoriesList}
        // defaultValue={selectedFactors.map(e => e.id)}
        allId={SELECTS_ID}
        handlerList={(values) => handleChangeFilter("categories", values)}
      />
      <DropDownDeadlineSelect
        type="deadline"
        title="Дней до мед. осмотра"
        handlerChangeFilter={(_, value) => handleChangeDeadline(value)}
      />
      <DatePicker
        label="Дата следующего мед. осмотра"
        onChange={handleChangeStartDate}
      />
    </div>
  );
};

type DatePickerPropsT = {
  label: string;
  onChange: (value: number[] | undefined) => void;
};

const DatePicker: FC<DatePickerPropsT> = ({ label, onChange }) => {
  const [isModalOpen, modal] = useDisclosure();
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [textDate, setTextDate] = useState("");

  const handleChange = (_: string, value: string[] | undefined) => {
    if (!value) {
      return onChange(undefined);
    }
    onChange(value.map((v) => parseInt(v)));
  };

  return (
    <div>
      <div className={styles.date} onClick={modal.open}>
        <div className={styles.date__label}>{label}</div>
        {textDate}
        <CalendarMonth color="action" className={styles.date__icon} />
      </div>
      <DatePeriod
        active={isModalOpen}
        closeModal={modal.close}
        setTextDate={setTextDate}
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        type="startDate"
        handlerChangeFilter={handleChange}
      />
    </div>
  );
};
